import React, { useCallback, useContext, useEffect, useRef, useState } from 'react'
import { Alert, Box, Checkbox, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material'
import { DialogWrapper } from '../../presentation-components/display-elements-components/dialog-wrapper'
import { LoadingSpinner } from '../../presentation-components/display-elements-components/loading-spinner'
import OrganisationService from '../../../shared/services/organisation/organisation.service'
import { IDistributionLossCorrectionPeriod } from '../../../shared/services/organisation/organisation.model'
import { UserNotificationsContext } from '../../../context/user-notifications/user-notifications'

interface DistributionLossCorrectionDialogProps {
  open: boolean;
  onClose: () => void;
}

const POLL_INTERVAL_MS = 2000
// A correction is a few minutes at worst. Past this the worker is not coming back, and
// polling forever would leave the user watching a spinner with no explanation.
const MAX_POLL_ATTEMPTS = 150

const formatKg = (value?: number | null): string => {
  const numeric = Number(value)
  if (!Number.isFinite(numeric)) return '-'
  if (Math.abs(numeric) >= 1000) return `${(numeric / 1000).toFixed(2)} t CO2e`
  return `${Math.round(numeric * 100) / 100} kg CO2e`
}

const periodLabel = (period: IDistributionLossCorrectionPeriod): string => {
  if (period.name) return period.name
  const from = period.startDate ? period.startDate.slice(0, 10) : '?'
  const to = period.endDate ? period.endDate.slice(0, 10) : '?'
  return `${from} to ${to}`
}

/**
 * Offered when an organisation turns on transmission and distribution losses.
 *
 * The setting only affects records calculated from that point on, so without this the
 * customer flips a switch and nothing happens. This asks whether they also want their
 * existing open periods brought up to date, and shows what each would cost before they
 * agree. Locked periods never appear: their figures are already disclosed.
 */
export const DistributionLossCorrectionDialog: React.FC<DistributionLossCorrectionDialogProps> = ({ open, onClose }) => {
  const [periods, setPeriods] = useState<IDistributionLossCorrectionPeriod[]>([])
  const [selectedIds, setSelectedIds] = useState<string[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')

  const organisationService = new OrganisationService()
  const userNotificationsContext = useContext(UserNotificationsContext)

  // Held in a ref so the interval can always be cleared, including on unmount. The
  // equivalent dialogs leak their interval if the user navigates away mid-job.
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const stopPolling = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current)
      pollRef.current = null
    }
  }, [])
  useEffect(() => stopPolling, [stopPolling])

  useEffect(() => {
    if (!open) return
    setIsLoading(true)
    setErrorMessage('')
    organisationService.getDistributionLossCorrectionPreview()
      .then(response => {
        const found = response.periods || []
        setPeriods(found)
        setSelectedIds(found.map(p => p.reportPeriodId))
      })
      .catch(() => setErrorMessage('Could not work out what would change. You can turn this on now and correct your periods later.'))
      .finally(() => setIsLoading(false))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  const toggle = (reportPeriodId: string) => {
    setSelectedIds(current => current.includes(reportPeriodId)
      ? current.filter(id => id !== reportPeriodId)
      : [...current, reportPeriodId])
  }

  const selected = periods.filter(p => selectedIds.includes(p.reportPeriodId))
  const totalKg = selected.reduce((sum, p) => sum + (Number(p.kgAddedToReports) || 0), 0)
  const totalVehicleRows = selected.reduce((sum, p) => sum + (Number(p.vehicleRowsRegrouped) || 0), 0)
  const totalUnmappable = selected.reduce((sum, p) => sum + (Number(p.unmappableRows) || 0), 0)

  const handleClose = () => {
    stopPolling()
    setErrorMessage('')
    onClose()
  }

  const handleApply = async () => {
    setIsSubmitting(true)
    setErrorMessage('')
    try {
      const response = await organisationService.startDistributionLossCorrection(selectedIds)
      if (!response?.success) {
        setErrorMessage(response?.message || 'Could not start the correction.')
        setIsSubmitting(false)
        return
      }

      const jobIds = (response.jobs || []).map(job => job.id)
      userNotificationsContext.openSnackbar({
        open: true,
        message: `Updating ${jobIds.length} report period${jobIds.length === 1 ? '' : 's'}. This runs in the background.`,
        duration: 0,
        backgroundColor: 'primary.main',
      })
      onClose()

      let attempts = 0
      stopPolling()
      pollRef.current = setInterval(async () => {
        attempts += 1
        if (attempts > MAX_POLL_ATTEMPTS) {
          stopPolling()
          userNotificationsContext.openSnackbar({
            open: true,
            message: 'The update is taking longer than expected. It is still running; check the report period shortly.',
            duration: 8000,
            backgroundColor: 'primary.main',
          })
          return
        }
        try {
          const { jobs } = await organisationService.getDistributionLossCorrectionJobs(jobIds)
          if (!jobs?.length || jobs.some(job => job.status === 'PENDING' || job.status === 'IN_PROGRESS')) return

          stopPolling()
          const failed = jobs.filter(job => job.status === 'FAILED')
          if (failed.length) {
            userNotificationsContext.openSnackbar({
              open: true,
              message: failed[0].errorMessage || 'The update failed for one or more report periods.',
              duration: 8000,
              backgroundColor: 'error.main',
            })
            return
          }
          const added = jobs.reduce((sum, job) => sum + (job.insertedRows || 0), 0)
          const moved = jobs.reduce((sum, job) => sum + (job.regroupedRows || 0), 0)
          const unmappable = jobs.reduce((sum, job) => sum + (job.unmappableRows || 0), 0)
          userNotificationsContext.openSnackbar({
            open: true,
            message: `Updated. ${added} calculation${added === 1 ? '' : 's'} added`
              + (moved ? `, ${moved} vehicle row${moved === 1 ? '' : 's'} moved to Category 3` : '')
              + (unmappable ? `. ${unmappable} record${unmappable === 1 ? '' : 's'} had no matching factor and were left unchanged` : '')
              + '.',
            duration: 6000,
            backgroundColor: 'success.main',
          })
        } catch (error) {
          console.error(error)
        }
      }, POLL_INTERVAL_MS)
    } catch (error: any) {
      setErrorMessage(error?.response?.data?.message || 'Could not start the correction.')
      setIsSubmitting(false)
    }
  }

  return (
    <DialogWrapper
      open={open}
      onClose={handleClose}
      title='Update your existing report periods?'
      cancelHandler={handleClose}
      submitHandler={handleApply}
      isSubmitting={isSubmitting}
      disableSaveButton={isLoading || selected.length === 0}
      maxWidth='md'
      submitButtonText='Update selected periods'
      submitButtonLoadingText='Starting'
    >
      {isLoading ? <LoadingSpinner /> : (
        <Box>
          <Typography variant='body2' sx={{ mb: 2 }}>
            Records you add or edit will include transmission and distribution losses. Your existing
            records keep their current figures unless you update them here.
          </Typography>

          {periods.length === 0 ? (
            <Alert severity='info'>
              Nothing to update. You have no electricity or district heat records in an open report
              period, so this applies to records you add or edit from here.
            </Alert>
          ) : (
            <>
              <Table size='small'>
                <TableHead>
                  <TableRow>
                    <TableCell padding='checkbox' />
                    <TableCell sx={{ fontWeight: 600 }}>Report period</TableCell>
                    <TableCell align='right' sx={{ fontWeight: 600 }}>Calculations added</TableCell>
                    <TableCell align='right' sx={{ fontWeight: 600 }}>Added to Category 3</TableCell>
                    <TableCell align='right' sx={{ fontWeight: 600 }}>Vehicle rows moved</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {periods.map(period => (
                    <TableRow key={period.reportPeriodId} hover>
                      <TableCell padding='checkbox'>
                        <Checkbox
                          checked={selectedIds.includes(period.reportPeriodId)}
                          onChange={() => toggle(period.reportPeriodId)}
                        />
                      </TableCell>
                      <TableCell>{periodLabel(period)}</TableCell>
                      <TableCell align='right'>{period.newLossRows}</TableCell>
                      <TableCell align='right'>{formatKg(period.kgAddedToReports)}</TableCell>
                      <TableCell align='right'>{period.vehicleRowsRegrouped || '-'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              <Box sx={{ mt: 2 }}>
                <Typography variant='body2' sx={{ fontWeight: 600 }}>
                  Selected: {formatKg(totalKg)} will be added to your reported Category 3.
                </Typography>
                {totalVehicleRows > 0 && (
                  <Typography variant='body2' sx={{ color: 'text.secondary', mt: 0.5 }}>
                    {totalVehicleRows} company vehicle upstream row{totalVehicleRows === 1 ? '' : 's'} will move from
                    Business Travel and Upstream Transportation into Category 3. This changes which category they
                    report under, not your Scope 3 total.
                  </Typography>
                )}
                {totalUnmappable > 0 && (
                  <Typography variant='body2' sx={{ color: 'text.secondary', mt: 0.5 }}>
                    {totalUnmappable} record{totalUnmappable === 1 ? ' has' : 's have'} no matching loss factor and
                    will be left unchanged.
                  </Typography>
                )}
                <Typography variant='body2' sx={{ color: 'text.secondary', mt: 0.5 }}>
                  Locked report periods are not listed and are never changed.
                </Typography>
              </Box>
            </>
          )}

          {errorMessage && <Alert severity='error' sx={{ mt: 2 }}>{errorMessage}</Alert>}
        </Box>
      )}
    </DialogWrapper>
  )
}
